import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { apiRequest } from '../config/api';
import { getCdnImageUrl } from '../utils/cdnImageUrl';
import PageLayout from '../components/PageLayout';
import './CDNPage.css';

interface CDNFile {
  name: string;
  path: string;
  size: number;
  uploadedBy?: string;
  uploadedAt?: string;
  isLatest: boolean;
  isFolder: boolean;
}

interface CDNFilesResponse {
  files: CDNFile[];
  folders: CDNFile[];
}

interface ConflictInfo {
  folder: string;
  filename: string;
  existingSize: number;
  existingUploadedAt: string;
  existingUploadedBy: string;
}

interface CheckConflictsResponse {
  newFiles: string[];
  existingFiles: string[];
  conflicts: ConflictInfo[];
}

interface UploadInfo {
  filename: string;
  uploadUrl: string;
  fileUrl: string;
  contentType: string;
  cacheControl: string;
  metadata?: { [key: string]: string };
  expiresIn: number;
}

interface UploadURLsResponse {
  uploads: UploadInfo[];
}

interface FileVersion {
  versionId: string;
  isLatest: boolean;
  size: number;
  uploadedBy?: string;
  uploadedAt: string;
}

interface FileVersionsResponse {
  versions: FileVersion[];
}

const CDNPage: React.FC = () => {
  const { token } = useAuth();
  const [files, setFiles] = useState<CDNFile[]>([]);
  const [folders, setFolders] = useState<CDNFile[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [currentFolder, setCurrentFolder] = useState('');
  const [breadcrumbs, setBreadcrumbs] = useState<string[]>([]);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showCreateFolderModal, setShowCreateFolderModal] = useState(false);
  const [showVersionHistory, setShowVersionHistory] = useState(false);
  const [selectedFile, setSelectedFile] = useState<CDNFile | null>(null);
  const [fileVersions, setFileVersions] = useState<FileVersion[]>([]);
  const [uploadProgress, setUploadProgress] = useState<{ [key: string]: number }>({});
  const [showDeleted, setShowDeleted] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [invalidating, setInvalidating] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [pendingUploads, setPendingUploads] = useState<File[]>([]);
  const [conflictInfo, setConflictInfo] = useState<CheckConflictsResponse | null>(null);
  const [sortField, setSortField] = useState<'name' | 'uploadedAt'>('name');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [filterUploadedBy, setFilterUploadedBy] = useState<string>('');

  useEffect(() => {
    if (token) {
      loadFiles();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, currentFolder]);

  const loadFiles = async () => {
    if (!token) return;
    try {
      setLoading(true);
      const params = currentFolder ? `?folder=${encodeURIComponent(currentFolder)}` : '';
      const response = await apiRequest<CDNFilesResponse>(
        `/admin/cdn/files${params}`,
        token
      );
      setFiles(response.files || []);
      setFolders(response.folders || []);
      updateBreadcrumbs();
      setError('');
    } catch (err: any) {
      setError(err.message || 'Failed to load files');
      setFiles([]);
      setFolders([]);
    } finally {
      setLoading(false);
    }
  };

  const updateBreadcrumbs = () => {
    if (!currentFolder) {
      setBreadcrumbs([]);
      return;
    }
    const parts = currentFolder.split('/').filter(p => p);
    setBreadcrumbs(parts);
  };

  const navigateToFolder = (folderPath: string) => {
    setCurrentFolder(folderPath);
  };

  // navigateUp function removed - not used

  const navigateToBreadcrumb = (index: number) => {
    const parts = breadcrumbs.slice(0, index + 1);
    setCurrentFolder(parts.join('/'));
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = e.target.files;
    if (!selectedFiles || selectedFiles.length === 0) return;

    const filesArray = Array.from(selectedFiles);
    setPendingUploads(filesArray);

    // Check for conflicts
    try {
      const checkRequest = {
        files: filesArray.map(file => ({
          folder: currentFolder,
          filename: file.name,
        })),
      };

      const conflictResponse = await apiRequest<CheckConflictsResponse>(
        '/admin/cdn/files/check-conflicts',
        token!,
        {
          method: 'POST',
          body: JSON.stringify(checkRequest),
        }
      );

      setConflictInfo(conflictResponse);
      setShowUploadModal(true);
    } catch (err: any) {
      setError(err.message || 'Failed to check conflicts');
    }
  };

  const handleUploadConfirm = async () => {
    if (!token || !pendingUploads.length) return;

    try {
      // Get presigned URLs
      const uploadRequest = {
        files: pendingUploads.map(file => ({
          folder: currentFolder,
          filename: file.name,
        })),
      };

      const uploadResponse = await apiRequest<UploadURLsResponse>(
        '/admin/cdn/files/upload-urls',
        token,
        {
          method: 'POST',
          body: JSON.stringify(uploadRequest),
        }
      );

      // Upload each file
      const uploadPromises = uploadResponse.uploads.map(async (uploadInfo, index) => {
        const file = pendingUploads[index];
        return uploadFile(file, uploadInfo);
      });

      await Promise.all(uploadPromises);

      // Refresh file list
      await loadFiles();
      setShowUploadModal(false);
      setPendingUploads([]);
      setConflictInfo(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (err: any) {
      setError(err.message || 'Failed to upload files');
    }
  };

  const uploadFile = async (file: File, uploadInfo: UploadInfo): Promise<void> => {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();

      xhr.upload.addEventListener('progress', (e) => {
        if (e.lengthComputable) {
          const percentComplete = (e.loaded / e.total) * 100;
          setUploadProgress((prev) => ({
            ...prev,
            [file.name]: percentComplete,
          }));
        }
      });

      xhr.addEventListener('load', () => {
        if (xhr.status === 200) {
          setUploadProgress((prev) => {
            const next = { ...prev };
            delete next[file.name];
            return next;
          });
          resolve();
        } else {
          reject(new Error(`Upload failed: ${xhr.statusText}`));
        }
      });

      xhr.addEventListener('error', () => {
        reject(new Error('Upload failed'));
      });

      xhr.open('PUT', uploadInfo.uploadUrl);
      xhr.setRequestHeader('Content-Type', uploadInfo.contentType);
      // Add metadata headers if present
      
      if (uploadInfo.cacheControl) {
        xhr.setRequestHeader('Cache-Control', uploadInfo.cacheControl);
      }

      if (uploadInfo.metadata) {
        Object.entries(uploadInfo.metadata).forEach(([key, value]) => {
          xhr.setRequestHeader(`x-amz-meta-${key}`, value);
        });
      }
      xhr.send(file);
    });
  };

  const handleDownload = (file: CDNFile) => {
    const fileUrl = getCdnImageUrl(file.path);
    // Create a temporary anchor element to trigger download
    const link = document.createElement('a');
    link.href = fileUrl;
    link.download = file.name;
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleDelete = async (file: CDNFile) => {
    if (!token || !window.confirm(`Delete ${file.name}? Previous versions will be preserved.`)) {
      return;
    }

    try {
      await apiRequest(`/admin/cdn/files?path=${encodeURIComponent(file.path)}`, token, {
        method: 'DELETE',
      });
      await loadFiles();
    } catch (err: any) {
      setError(err.message || 'Failed to delete file');
    }
  };

  const handleCreateFolder = async () => {
    if (!token || !newFolderName.trim()) return;

    try {
      const baseFolder = currentFolder.replace(/\/+$/, '');
      const folderPath = baseFolder
        ? `${baseFolder}/${newFolderName.trim()}`
        : newFolderName.trim();

      // Create folder by uploading an empty file (S3 doesn't have true folders)
      // We'll use a placeholder approach - create a .keep file or similar
      // Actually, S3 folders are created automatically when files are uploaded
      // So we'll just create an empty marker file
      const createRequest = {
        files: [
          {
            folder: folderPath,
            filename: '.keep',
          },
        ],
      };

      const uploadResponse = await apiRequest<UploadURLsResponse>(
        '/admin/cdn/files/upload-urls',
        token,
        {
          method: 'POST',
          body: JSON.stringify(createRequest),
        }
      );

      if (uploadResponse.uploads.length > 0) {
        const uploadInfo = uploadResponse.uploads[0];
        const xhr = new XMLHttpRequest();
        xhr.open('PUT', uploadInfo.uploadUrl);
        xhr.setRequestHeader('Content-Type', uploadInfo.contentType);
        if (uploadInfo.cacheControl) {
          xhr.setRequestHeader('Cache-Control', uploadInfo.cacheControl);
        }
        // Add metadata headers if present
        if (uploadInfo.metadata) {
          Object.entries(uploadInfo.metadata).forEach(([key, value]) => {
            xhr.setRequestHeader(`x-amz-meta-${key}`, value);
          });
        }
        xhr.send(new Blob([''], { type: uploadInfo.contentType }));

        xhr.addEventListener('load', async () => {
          if (xhr.status === 200) {
            await loadFiles();
            setShowCreateFolderModal(false);
            setNewFolderName('');
          }
        });
      }
    } catch (err: any) {
      setError(err.message || 'Failed to create folder');
    }
  };

  const handleViewHistory = async (file: CDNFile) => {
    if (!token) return;

    try {
      const response = await apiRequest<FileVersionsResponse>(
        `/admin/cdn/files/versions?path=${encodeURIComponent(file.path)}`,
        token
      );
      setFileVersions(response.versions || []);
      setSelectedFile(file);
      setShowVersionHistory(true);
    } catch (err: any) {
      setError(err.message || 'Failed to load version history');
    }
  };

  const handleRestore = async (versionId: string) => {
    if (!token || !selectedFile) return;

    if (!window.confirm('Restore this version? This will create a new version with the selected content.')) {
      return;
    }

    try {
      await apiRequest(
        `/admin/cdn/files/restore`,
        token,
        {
          method: 'POST',
          body: JSON.stringify({ 
            path: selectedFile.path,
            versionId 
          }),
        }
      );
      await loadFiles();
      await handleViewHistory(selectedFile);
    } catch (err: any) {
      setError(err.message || 'Failed to restore version');
    }
  };

  const handleInvalidate = async () => {
    if (!token) return;

    if (!window.confirm('Invalidate CloudFront cache? This will clear all cached content.')) {
      return;
    }

    try {
      setInvalidating(true);
      await apiRequest('/admin/cdn/invalidate', token, {
        method: 'POST',
        body: JSON.stringify({ paths: ['/*'] }),
      });
      alert('Cache invalidation started successfully');
    } catch (err: any) {
      setError(err.message || 'Failed to invalidate cache');
    } finally {
      setInvalidating(false);
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const formatDate = (dateString?: string): string => {
    if (!dateString) return '';
    try {
      return new Date(dateString).toLocaleString();
    } catch {
      return dateString;
    }
  };

  const isImageFile = (filename: string): boolean => {
    return /\.(png|jpg|jpeg|gif|svg|webp)$/i.test(filename);
  };

  const handleSort = (field: 'name' | 'uploadedAt') => {
    if (sortField === field) {
      // Toggle direction if clicking the same field
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // Set new field and default to ascending
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const getSortedAndFilteredFiles = (): CDNFile[] => {
    let filtered = files;

    // Filter by uploadedBy
    if (filterUploadedBy) {
      filtered = filtered.filter(file => file.uploadedBy === filterUploadedBy);
    }

    // Sort files
    const sorted = [...filtered].sort((a, b) => {
      let comparison = 0;

      if (sortField === 'name') {
        comparison = a.name.localeCompare(b.name);
      } else if (sortField === 'uploadedAt') {
        const dateA = a.uploadedAt ? new Date(a.uploadedAt).getTime() : 0;
        const dateB = b.uploadedAt ? new Date(b.uploadedAt).getTime() : 0;
        comparison = dateA - dateB;
      }

      return sortDirection === 'asc' ? comparison : -comparison;
    });

    return sorted;
  };

  const getUniqueUploaders = (): string[] => {
    const uploaders = new Set<string>();
    files.forEach(file => {
      if (file.uploadedBy) {
        uploaders.add(file.uploadedBy);
      }
    });
    return Array.from(uploaders).sort();
  };

  const sortedAndFilteredFiles = getSortedAndFilteredFiles();

  return (
    <PageLayout title="CDN Management">
      <div className="cdn-page">
        {error && <div className="error-banner">{error}</div>}

        <div className="cdn-toolbar">
          <div className="cdn-actions">
            <button className="btn-primary" onClick={() => fileInputRef.current?.click()}>
              Upload Files
            </button>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              style={{ display: 'none' }}
              onChange={handleFileSelect}
            />
            <button className="btn-secondary" onClick={() => setShowCreateFolderModal(true)}>
              Create Folder
            </button>
            <button
              className="btn-secondary"
              onClick={handleInvalidate}
              disabled={invalidating}
            >
              {invalidating ? 'Invalidating...' : 'Invalidate Cache'}
            </button>
          </div>
          <div className="cdn-filters">
            <label>
              Filter by Uploader:
              <select
                value={filterUploadedBy}
                onChange={(e) => setFilterUploadedBy(e.target.value)}
                style={{ marginLeft: '0.5rem', padding: '0.5rem' }}
              >
                <option value="">All Uploaders</option>
                {getUniqueUploaders().map(uploader => (
                  <option key={uploader} value={uploader}>
                    {uploader}
                  </option>
                ))}
              </select>
            </label>
            <label style={{ marginLeft: '1rem' }}>
              <input
                type="checkbox"
                checked={showDeleted}
                onChange={(e) => setShowDeleted(e.target.checked)}
              />
              Show Deleted
            </label>
          </div>
        </div>

        <div className="cdn-breadcrumbs">
          <button
            className="breadcrumb-link"
            onClick={() => setCurrentFolder('')}
            disabled={!currentFolder}
          >
            Root
          </button>
          {breadcrumbs.map((crumb, index) => (
            <React.Fragment key={index}>
              <span className="breadcrumb-separator">/</span>
              <button
                className="breadcrumb-link"
                onClick={() => navigateToBreadcrumb(index)}
              >
                {crumb}
              </button>
            </React.Fragment>
          ))}
        </div>

        {loading ? (
          <div className="loading">Loading...</div>
        ) : (
          <div className="cdn-content">
            <div className="cdn-files-list">
              {folders.map((folder) => (
                <div key={folder.path} className="cdn-item folder-item">
                  <div className="item-icon">📁</div>
                  <div className="item-info">
                    <div className="item-name" onClick={() => navigateToFolder(folder.path)}>
                      {folder.name}
                    </div>
                  </div>
                </div>
              ))}

              {files.length > 0 && (
                <div className="cdn-table-header">
                  <div className="table-header-cell table-header-icon"></div>
                  <div 
                    className="table-header-cell table-header-name sortable"
                    onClick={() => handleSort('name')}
                  >
                    Name
                    {sortField === 'name' && (
                      <span className="sort-indicator">
                        {sortDirection === 'asc' ? ' ▲' : ' ▼'}
                      </span>
                    )}
                  </div>
                  <div 
                    className="table-header-cell table-header-date sortable"
                    onClick={() => handleSort('uploadedAt')}
                  >
                    Upload Date
                    {sortField === 'uploadedAt' && (
                      <span className="sort-indicator">
                        {sortDirection === 'asc' ? ' ▲' : ' ▼'}
                      </span>
                    )}
                  </div>
                  <div className="table-header-cell table-header-actions">Actions</div>
                </div>
              )}

              {sortedAndFilteredFiles.map((file) => (
                <div key={file.path} className="cdn-item file-item">
                  <div className="item-icon">
                    {isImageFile(file.name) ? (
                      <img
                        src={getCdnImageUrl(file.path)}
                        alt={file.name}
                        className="thumbnail-image"
                        onError={(e) => {
                          // Fallback to emoji if image fails to load
                          const target = e.target as HTMLImageElement;
                          target.style.display = 'none';
                          if (target.parentElement) {
                            target.parentElement.textContent = '🖼️';
                          }
                        }}
                      />
                    ) : (
                      '📄'
                    )}
                  </div>
                  <div className="item-info">
                    <div className="item-name">{file.name}</div>
                    <div className="item-meta">
                      {formatFileSize(file.size)}
                      {file.uploadedBy && ` • Uploaded by ${file.uploadedBy}`}
                    </div>
                  </div>
                  <div className="item-date">
                    {file.uploadedAt ? formatDate(file.uploadedAt) : '-'}
                  </div>
                  <div className="item-actions">
                    <button
                      className="btn-link"
                      onClick={() => handleDownload(file)}
                      title="Download"
                    >
                      Download
                    </button>
                    <button
                      className="btn-link"
                      onClick={() => handleViewHistory(file)}
                      title="View History"
                    >
                      History
                    </button>
                    <button
                      className="btn-link btn-danger"
                      onClick={() => handleDelete(file)}
                      title="Delete"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              ))}

              {sortedAndFilteredFiles.length === 0 && folders.length === 0 && (
                <div className="empty-state">No files or folders</div>
              )}
              {sortedAndFilteredFiles.length === 0 && files.length > 0 && (
                <div className="empty-state">No files match the current filter</div>
              )}
            </div>
          </div>
        )}

        {/* Upload Confirmation Modal */}
        {showUploadModal && conflictInfo && (
          <div className="modal-overlay" onClick={() => setShowUploadModal(false)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <h3>Confirm Upload</h3>
              <div className="upload-warning">
                <strong>⚠️ Warning:</strong> Any files uploaded here are public to the internet.
              </div>

              {conflictInfo?.newFiles && conflictInfo.newFiles.length > 0 && (
                <div className="upload-summary">
                  <h4>Uploading {conflictInfo.newFiles.length} new file(s):</h4>
                  <ul>
                    {conflictInfo.newFiles.map((filename) => (
                      <li key={filename}>{filename}</li>
                    ))}
                  </ul>
                </div>
              )}

              {conflictInfo?.existingFiles && conflictInfo.existingFiles.length > 0 && (
                <div className="upload-summary">
                  <h4>Replacing {conflictInfo.existingFiles.length} file(s):</h4>
                  <ul>
                    {conflictInfo.conflicts.map((conflict) => (
                      <li key={conflict.filename}>
                        {conflict.filename} (existing: {formatFileSize(conflict.existingSize)}, uploaded{' '}
                        {formatDate(conflict.existingUploadedAt)} by {conflict.existingUploadedBy})
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="modal-actions">
                <button className="btn-secondary" onClick={() => setShowUploadModal(false)}>
                  Cancel
                </button>
                <button className="btn-primary" onClick={handleUploadConfirm}>
                  Proceed
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Upload Progress */}
        {Object.keys(uploadProgress).length > 0 && (
          <div className="upload-progress-container">
            <h4>Upload Progress</h4>
            {Object.entries(uploadProgress).map(([filename, progress]) => (
              <div key={filename} className="upload-progress-item">
                <div className="upload-progress-label">{filename}</div>
                <div className="upload-progress-bar">
                  <div
                    className="upload-progress-fill"
                    style={{ width: `${progress}%` }}
                  />
                </div>
                <div className="upload-progress-percent">{Math.round(progress)}%</div>
              </div>
            ))}
          </div>
        )}

        {/* Create Folder Modal */}
        {showCreateFolderModal && (
          <div className="modal-overlay" onClick={() => setShowCreateFolderModal(false)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <h3>Create Folder</h3>
              <div className="form-group">
                <label>Folder Name</label>
                <input
                  type="text"
                  value={newFolderName}
                  onChange={(e) => setNewFolderName(e.target.value)}
                  placeholder="Enter folder name"
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      handleCreateFolder();
                    }
                  }}
                />
              </div>
              <div className="modal-actions">
                <button
                  className="btn-secondary"
                  onClick={() => setShowCreateFolderModal(false)}
                >
                  Cancel
                </button>
                <button className="btn-primary" onClick={handleCreateFolder}>
                  Create
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Version History Modal */}
        {showVersionHistory && selectedFile && (
          <div className="modal-overlay" onClick={() => setShowVersionHistory(false)}>
            <div className="modal-content version-history-modal" onClick={(e) => e.stopPropagation()}>
              <h3>Version History: {selectedFile.name}</h3>
              <div className="version-list">
                {fileVersions.map((version) => (
                  <div key={version.versionId} className="version-item">
                    <div className="version-info">
                      <div className="version-header">
                        {version.isLatest && <span className="version-badge">Current</span>}
                        <span className="version-date">{formatDate(version.uploadedAt)}</span>
                      </div>
                      <div className="version-meta">
                        {formatFileSize(version.size)}
                        {version.uploadedBy && ` • Uploaded by ${version.uploadedBy}`}
                      </div>
                    </div>
                    {!version.isLatest && (
                      <button
                        className="btn-link"
                        onClick={() => handleRestore(version.versionId)}
                      >
                        Restore
                      </button>
                    )}
                  </div>
                ))}
              </div>
              <div className="modal-actions">
                <button className="btn-secondary" onClick={() => setShowVersionHistory(false)}>
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </PageLayout>
  );
};

export default CDNPage;

